import { List, useTheme } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import invariant from 'invariant'

import { Box, Icons, RadioListItem, Text } from '../../components'
import { toAmountString } from '../../utils'
import { paymentMethods } from './payment'
import { guestPaymentMethods, GUEST_PAYMENT_METHOD_TYPE } from './payment-guest/index'

export function PaymentMethodRadioList({
  control,
  setValue,
  fund,
  hideRadio,
  required = false,
  isGuest,
  charityOverrides = {},
}) {
  const paymentMethodsToUse = isGuest ? guestPaymentMethods : paymentMethods
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const theme = useTheme()

  // Could be better if there's a UX way to indicate that the charity has disabled some payment methods.
  const enabledMethods = paymentMethodsToUse.filter((pm) => !charityOverrides.disabledMethods?.includes(pm.value))

  const futureGiftOrder = [
    GUEST_PAYMENT_METHOD_TYPE.flowThroughShares,
    GUEST_PAYMENT_METHOD_TYPE.sharesOfPrivateCorporations,
    GUEST_PAYMENT_METHOD_TYPE.insuranceGifts,
    GUEST_PAYMENT_METHOD_TYPE.estateGifts,
  ]

  const firstFutureGift = enabledMethods.find((method) => futureGiftOrder.includes(method.value))?.value

  const listRef = useRef()

  const checkScrollIndicator = useCallback(() => {
    const listEl = listRef.current
    if (listEl) {
      const hasScrollableContent = listEl.scrollHeight > listEl.clientHeight
      const isScrolledToBottom = listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - 5
      setShowScrollIndicator(hasScrollableContent && !isScrolledToBottom)
    }
  }, [])

  useEffect(() => {
    /** @type {HTMLUListElement|undefined} */
    const listEl = listRef.current
    invariant(listEl, 'listRef.current not defined')

    const checkedRadio = listEl.querySelector('input[type="radio"]:checked')
    if (checkedRadio) {
      // When a radio element is checked on mount, auto-scroll to the active list item
      const listItemEl = checkedRadio.closest('label')
      listEl.scrollTo({
        behavior: 'instant',
        top: Math.max(listItemEl.offsetTop + listItemEl.clientHeight - listEl.clientHeight, 0),
      })
    }

    // Check scroll indicator on mount
    checkScrollIndicator()

    const handleScroll = () => checkScrollIndicator()
    listEl.addEventListener('scroll', handleScroll)

    return () => {
      listEl.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Separate effect to check scroll indicator when methods change
  useEffect(() => {
    checkScrollIndicator()
  }, [enabledMethods, checkScrollIndicator])

  return (
    <>
      <Box>
        {required ? (
          <Text.H6>Choose a payment method for this donation: *</Text.H6>
        ) : (
          <Text.H6>You can make your donation via...</Text.H6>
        )}
        <Text.AltBody sx={{ fontStyle: 'italic', color: 'grey.600' }}>Please scroll to see all options</Text.AltBody>
      </Box>

      <Box sx={{ position: 'relative' }}>
        <List
          ref={listRef}
          sx={{
            width: '100%',
            // mb: 2,
            maxHeight: 200,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              '-webkit-appearance': 'none',
              width: '12px',
              display: 'block',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'block',
            },
            ...(showScrollIndicator
              ? {
                  borderBottom: '3px solid',
                  borderBottomColor: 'primary.main',
                }
              : {
                  borderBottom: '3px solid',
                  borderBottomColor: 'divider',
                }),
          }}
        >
          <Text.Bold sx={{ mb: 1 }}>Give Now</Text.Bold>

          {fund && (
            <RadioListItem
              control={control}
              name="paymentMethodType"
              value="wallet"
              hideRadio={hideRadio}
              beforeChange={() => {
                setValue('paymentMethodId', '')
              }}
              icon={<Icons.Wallet />}
              label="Wallet"
              helperText={`Gift up to ${toAmountString(fund.givingWalletBalance ?? 0)} from the "${
                fund.name
              }" Giving Wallet balance.`}
              required={required}
            />
          )}
          {enabledMethods.map((paymentMethod, index) => {
            const shouldShowSubheader =
              paymentMethod.value === firstFutureGift && futureGiftOrder.includes(paymentMethod.value)

            return (
              <>
                {shouldShowSubheader && <Text.Bold sx={{ mb: 1, mt: 2 }}>Plan a Future Gift</Text.Bold>}
                <RadioListItem
                  control={control}
                  name="paymentMethodType"
                  value={paymentMethod.value}
                  hideRadio={hideRadio}
                  beforeChange={() => setValue('paymentMethodId', '')}
                  icon={paymentMethod.icon}
                  label={paymentMethod.label}
                  helperText={paymentMethod.helperText}
                  popoverText={paymentMethod.popoverText}
                  required={required}
                />
              </>
            )
          })}
        </List>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          {showScrollIndicator ? (
            <Icons.ChevronDown style={{ color: theme.palette.primary.main, fontSize: '2em' }} />
          ) : (
            <Box sx={{ height: '2em' }} />
          )}
        </Box>
      </Box>
    </>
  )
}
